import { executeQuery } from "src/store";
import { baseService } from "src/store/base.store";
import { callFunctionSafe } from "src/firebase/functions";
import { notificationHealthRepository } from "./notification-health.repository";
import {
  NotificationHealth,
  RepairNotificationHealthResponse,
  RecheckNotificationHealthResponse,
} from "./types/notification-health.entity";

const callNotificationHealthFunction = async <T>(
  functionName: "recheckNotificationHealth" | "repairNotificationHealth",
  userId: string
) => {
  try {
    const response = await callFunctionSafe<T>(functionName, { userId });

    if ("data" in response) {
      return { data: response.data };
    }

    return {
      error: {
        status: 500,
        data: "Failed to update notification routing",
      },
    };
  } catch (error) {
    return {
      error: {
        status: 500,
        data:
          error instanceof Error
            ? error.message
            : "Failed to update notification routing",
      },
    };
  }
};

const notificationHealthService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationHealth: builder.query<
      (NotificationHealth & { id: string })[],
      { offset?: string; limit?: number }
    >({
      queryFn: async ({ offset, limit }) => {
        const result = await executeQuery(() =>
          notificationHealthRepository.find({ offset, limit, orderBy: ["checkedAt", "desc"] })
        );

        if ("error" in result) return { error: result.error } as any;
        return { data: result.data as (NotificationHealth & { id: string })[] };
      },
      providesTags: ["NotificationHealth"],
    }),
    countNotificationHealth: builder.query<number, void>({
      queryFn: () => executeQuery(() => notificationHealthRepository.count()) as any,
      providesTags: ["NotificationHealth"],
    }),
    recheckNotificationHealth: builder.mutation<RecheckNotificationHealthResponse, string>({
      queryFn: (userId) =>
        callNotificationHealthFunction<RecheckNotificationHealthResponse>(
          "recheckNotificationHealth",
          userId
        ),
      invalidatesTags: ["NotificationHealth"],
    }),
    repairNotificationHealth: builder.mutation<RepairNotificationHealthResponse, string>({
      queryFn: (userId) =>
        callNotificationHealthFunction<RepairNotificationHealthResponse>(
          "repairNotificationHealth",
          userId
        ),
      invalidatesTags: ["NotificationHealth"],
    }),
  }),
});

export const {
  useGetNotificationHealthQuery,
  useCountNotificationHealthQuery,
  useRecheckNotificationHealthMutation,
  useRepairNotificationHealthMutation,
} = notificationHealthService;
